<script setup lang="ts">
import {
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsRoot,
  TabsTrigger,
} from 'reka-ui'
import type { UmbracoProductVariantReformed } from '~~/server/lib/umbraco.types'

export interface PricingSubscription {
  id: string
  name: string
  properties: {
    shortTitle?: string | null
    interval?: 'Monthly' | 'Yearly' | 'Halfyear' | null
    price: {
      withTax: number
    }
  }
  effetciveMonthlyPrice: number
}

interface Props {
  subscriptions: PricingSubscription[] | UmbracoProductVariantReformed[]
  quantity?: number
  locale?: string
  showHelp?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  quantity: 1,
  locale: 'da',
  showHelp: true
})

// Get the quantity, default to 1 if not provided
const selectedQuantity = computed(() => props.quantity || 1)

// Helper function to get the billing period text
const getBillingPeriodText = (interval: string | null) => {
  if (props.locale === 'en') {
    switch (interval) {
      case 'Monthly':
        return 'monthly'
      case 'Yearly':
        return 'yearly'
      case 'Halfyear':
        return 'half-yearly'
      default:
        return 'monthly'
    }
  } else {
    switch (interval) {
      case 'Monthly':
        return 'månedlig'
      case 'Yearly':
        return 'årlig'
      case 'Halfyear':
        return 'halvårlig'
      default:
        return 'månedlig'
    }
  }
}

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('da-DK', {
    style: 'currency',
    currency: 'DKK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

// Localization strings
const texts = {
  da: {
    minimumPrice: 'Mindstepris (6 mdr.)',
    tax: 'Heraf moms (25%)',
    readMore: 'Læs mere omkring',
    termsAndConditions: 'Vilkår og betingelser',
    bindingPeriod: 'Bindingsperiode',
    months: 'måneder',
    payment: 'betaling',
    minimumPriceFor: 'Mindstepris for Saphe',
    premium: 'Premium',
    plus: '+',
    piecesEach: 'stk á',
    minimumPriceExplanation: (shortTitle: string, price: string) =>
      `Premium+ med <strong class="lowercase font-medium">${shortTitle.toLowerCase()}</strong> betaling har en mindstepris på <strong class="font-medium">${price}</strong>, da alle nye abonnementer har en bindingsperiode på 6 måneder.`
  },
  en: {
    minimumPrice: 'Minimum price (6 months)',
    tax: 'VAT included (25%)',
    readMore: 'Read more about',
    termsAndConditions: 'Terms and conditions',
    bindingPeriod: 'Binding period',
    months: 'months',
    payment: 'payment',
    minimumPriceFor: 'Minimum price for Saphe',
    premium: 'Premium',
    plus: '+',
    piecesEach: 'pcs at',
    minimumPriceExplanation: (shortTitle: string, price: string) =>
      `Premium+ with <strong class="lowercase font-medium">${shortTitle.toLowerCase()}</strong> payment has a minimum price of <strong class="font-medium">${price}</strong>, as all new subscriptions have a 6-month binding period.`
  }
}

const t = (key: string, ...args: string[]) => {
  const currentTexts = texts[props.locale as keyof typeof texts] || texts.da
  const text = currentTexts[key as keyof typeof currentTexts]
  if (typeof text === 'function') {
    return (text as (...args: string[]) => string)(...args)
  }
  return text || key
}
</script>

<template>
  <div class="flex flex-col gap-6 items-start text-action-primary">
    <div class="text-xl font-normal">
      <span>{{ t('minimumPriceFor') }}</span> <span class="s-type">{{ t('premium') }}</span><span class="text-accent">{{ t('plus') }}</span>
    </div>

    <div class="w-full">
      <TabsRoot :defaultValue="subscriptions?.[0]?.id" class="w-full">
        <TabsList
          v-if="subscriptions?.length > 1"
          class="p-1 bg-tertiary/12 rounded-full w-full flex items-center relative"
        >
          <TabsIndicator
            class="bg-white absolute top-1 left-0 rounded-full h-[calc(100%-8px)] w-[var(--reka-tabs-indicator-size)] translate-x-[var(--reka-tabs-indicator-position)] transition-all duration-200"
          />
          <TabsTrigger
            class="flex-grow relative z-10 flex text-sm items-center justify-center py-1 cursor-pointer data-[state=active]:font-semibold"
            v-for="subscription in subscriptions"
            :key="subscription.id"
            :value="subscription.id"
          >
            {{ subscription.properties.shortTitle }}
          </TabsTrigger>
        </TabsList>

        <TabsContent
          :value="subscription.id"
          v-for="subscription in subscriptions"
          :key="subscription.id"
          class="py-6"
        >
          <div class="flex flex-col gap-4">
            <!-- Minimum price explanation -->
            <p class="text-sm leading-snug" v-html="t('minimumPriceExplanation',
                subscription.properties.shortTitle || getBillingPeriodText(subscription.properties.interval || null),
                formatCurrency(6 * subscription.effetciveMonthlyPrice * selectedQuantity)
              )">
            </p>

            <!-- Pricing breakdown -->
            <div class="flex flex-col gap-2 border-t border-tertiary-500/34 pt-4">
              <!-- Subscription price -->
              <div>
                <div class="flex justify-between items-center text-sm font-bold text-black">
                  <span>
                    {{ subscription.properties.shortTitle }} {{ t('payment') }}:
                  </span>
                  <span>{{ formatCurrency(subscription.properties.price.withTax * selectedQuantity) }}</span>
                </div>

                <div class="text-sm"><span v-if="selectedQuantity > 1">{{ selectedQuantity }} {{ t('piecesEach') }} {{ formatCurrency(subscription.properties.price.withTax) }}</span></div>
              </div>

              <!-- Binding period -->
              <div class="flex justify-between items-center text-sm font-bold text-black">
                <span>{{ t('bindingPeriod') }}:</span>
                <span>6 {{ t('months') }}</span>
              </div>

              <!-- Minimum price breakdown -->
              <div class="flex flex-col gap-2">
                <div>
                  <div class="flex justify-between items-center text-sm font-bold text-black">
                    <span>{{ t('minimumPrice') }}:</span>
                    <span>{{ formatCurrency(6 * subscription.effetciveMonthlyPrice * selectedQuantity) }}</span>
                  </div>
                  <div class="text-sm"><span v-if="selectedQuantity > 1">{{ selectedQuantity }} {{ t('piecesEach') }} {{ formatCurrency(6 * subscription.effetciveMonthlyPrice) }}</span></div>
                </div>
                <div class="flex justify-between items-center text-sm font-light text-black">
                  <span>{{ t('tax') }}</span>
                  <span>{{ formatCurrency(6 * subscription.effetciveMonthlyPrice * selectedQuantity * 0.2) }}</span>
                </div>
              </div>
            </div>

            <!-- Footer links -->
            <div class="pt-4 text-sm font-light [&_a]:underline [&_a]:hover:text-tertiary-950/50">
              {{ t('readMore') }}
              <a href="https://checkout.saphe.com/da/terms-and-conditions/" target="_blank" class="hover:underline">{{ t('termsAndConditions') }}</a>
            </div>
          </div>
        </TabsContent>
      </TabsRoot>
    </div>

    <ModulesShopSidebarHelp v-if="showHelp" />
  </div>
</template>
